import { useEffect } from 'react'
import * as actions from '@actions'
import useUpsertTractViewModel from './UpsertTractViewModel'

import CustomModal from '@/components/shared/CustomModal/CustomModal'
import { TextInput } from '@components/shared/TextInput'
import TextArea from '@components/shared/TextArea'
import { Message } from '@components/shared/Message'
import { Selectbox } from '@components/shared/Selectbox'

import './Tracts.css'

export const UpsertTract = ({
    showAdd,
    showHideAdd,
    editTract,
    runsheetId,
    runsheetTabId,
    onSuccess,
    initialName = '',
}) => {
    
    const {
        values,
        optionMaps,
        getSelectboxValue,
        getSelectboxOptions,
        handleChange,
        handleSelectboxChange,
        handleSubmit,
        message,
        setMessage,
        isValid,
    } = useUpsertTractViewModel(showHideAdd, editTract ? editTract : {
        name: initialName,
        runsheet: { id: runsheetId },
        runsheetTab: { id: runsheetTabId },
    }, onSuccess)
    
    useEffect(() => {
        actions.fetchStates()
    }, [])
    
    const handleModal = () => {
        showHideAdd(!showAdd)
    }
    
    return (
        
        <CustomModal open={showAdd} modalHandler={() => handleModal()} center
            className="!max-w-[80vw] 2xl:!max-w-[60vw]">
            
            <form onSubmit={handleSubmit}>
                
                <div className="modal-header">
                    <h2 className="modal-title">{editTract ? 'Change ' : 'Create'} Tract</h2>
                </div>
                
                <div className="modal-body !md:max-h-[70vh] Tracts">
                    
                    {message && <Message onDismiss={() => setMessage(null)} message={message} />}
                    
                    <div className="grid grid-cols-2 gap-4">
                        
                        <section className="row-span-2 flex flex-col gap-2">
                            <header className="mb-2 font-bold">Tract Details</header>
                            
                            <TextInput
                                name="name"
                                inputClassName={`${!isValid && !values.name ? 'input w-full input-error' : ''}`}
                                overrideInputClassName={!isValid && !values.name}
                                value={values.name || ''}
                                handleChange={handleChange}
                                label="Short Tract Name" />
                            
                            <Selectbox
                                label="State"
                                name="state"
                                className={`${!isValid && !values.state ? 'input-error' : ''}`}
                                option={getSelectboxValue('states', values.state)}
                                options={Object.values(optionMaps.states)}
                                handleChange={handleSelectboxChange} />
                            
                            <Selectbox
                                label="County"
                                name="countyId"
                                className={`${!isValid && !values.countyId ? 'input-error' : ''}`}
                                option={getSelectboxValue('counties', values.countyId)}
                                options={getSelectboxOptions('counties', optionMaps.states[values.state]?.list)}
                                handleChange={handleSelectboxChange} />
                            
                            <TextInput
                                label="Survey"
                                name="survey"
                                value={values.survey}
                                handleChange={handleChange} />
                            
                            <TextInput
                                label="Range / Block"
                                name="range"
                                value={values.range}
                                handleChange={handleChange} />
                            
                            <TextInput
                                label="Township"
                                name="township"
                                value={values.township}
                                handleChange={handleChange} />
                            
                            <TextInput
                                label="Section"
                                name="section"
                                value={values.section}
                                handleChange={handleChange} />
                            
                            <TextInput
                                label="Abstract"
                                name="abstract"
                                value={values.abstract}
                                handleChange={handleChange} />
                        
                        </section>
                        
                        <section>
                            
                            <header className="mb-4 font-bold">Subdivision Details</header>
                            
                            <div className="grid grid-cols-3 grid-rows-5 gap-2">
                                
                                <TextInput
                                    name="section1"
                                    value={values.section1}
                                    handleChange={handleChange}
                                    label="1/4 Section 1" />
                                <TextInput
                                    name="grossAcreage"
                                    className="col-span-2"
                                    value={values.grossAcreage}
                                    handleChange={handleChange}
                                    label="Gross Acres" />
                                
                                <TextInput
                                    name="section2"
                                    value={values.section2}
                                    handleChange={handleChange}
                                    label="1/4 Section 2" />
                                <TextInput
                                    name="lot"
                                    className="col-span-2"
                                    value={values.lot}
                                    handleChange={handleChange}
                                    label="Lot" />
                                
                                <TextInput
                                    name="section3"
                                    value={values.section3}
                                    handleChange={handleChange}
                                    label="1/4 Section 3" />
                                <TextInput
                                    name="block"
                                    className="col-span-2"
                                    value={values.block}
                                    handleChange={handleChange}
                                    label="Block" />
                                
                                <TextInput
                                    name="section4"
                                    value={values.section4}
                                    handleChange={handleChange}
                                    label="1/4 Section 4" />
                                <TextInput
                                    name="subdivision"
                                    className="col-span-2"
                                    value={values.subdivision}
                                    handleChange={handleChange}
                                    label="Subdivision" />
                                
                                <TextInput
                                    name="platBook"
                                    value={values.platBook}
                                    handleChange={handleChange}
                                    label="Plat Book" />
                                <TextInput
                                    name="platVolume"
                                    value={values.platVolume}
                                    handleChange={handleChange}
                                    label="Plat Volume" />
                                <TextInput
                                    name="platPage"
                                    value={values.platPage}
                                    handleChange={handleChange}
                                    label="Plat Page" />
                            </div>
                        </section>
                        
                        <div className="flex flex-col h-[200px]">
                            <label htmlFor="inputDescription">Full Legal Description</label>
                            <TextArea
                                id="inputDescription"
                                className="input flex-1"
                                name="description"
                                value={values.description}
                                onChange={handleChange} />
                        </div>
                    </div>
                </div>
                
                <div className="modal-footer flex justify-end gap-4 mt-4">
                    <button
                        type="button"
                        className="btn btn-primary btn-outline"
                        onClick={handleModal}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                        {editTract ? 'Update ' : 'Create'} Tract
                    </button>
                </div>
            
            </form>
        
        </CustomModal>
        
    )
    
}
